import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Minimal scroll manager for the public SPA, built entirely on the existing
 * react-router-dom hooks (no new library, no behavior change to history itself):
 *
 * - PUSH / REPLACE (clicking a link, programmatic navigate() to a route the user
 *   wasn't already on) -> scroll to the top of the new page. This is what fixes the
 *   confirmed bug: without this, a client-side route change keeps the OLD scrollY,
 *   which the browser then just clamps to the new (often shorter) page's height
 *   instead of resetting to 0 - e.g. homepage scrolled to 2500px -> click "Daire" ->
 *   /kategori/daire opens already at ~600px instead of the top.
 * - POP (browser Back/Forward) -> do nothing here and let the browser's native
 *   per-history-entry scroll memory (history.scrollRestoration = "auto", which is the
 *   default and is NOT changed anywhere in this app) put the user back roughly where
 *   they were. This is intentional - we only correct PUSH, never POP.
 * - #hash in the URL (e.g. the homepage's "#kategoriler" hero CTA) always wins over the
 *   scroll-to-top behavior - we scroll to that section instead, same as plain in-page
 *   anchor behavior would.
 */
const ScrollManager = () => {
  const location = useLocation();
  const navigationType = useNavigationType();
  const isFirstRender = useRef(true);

  useEffect(() => {
    // Never touch scroll on the very first mount (the initial hard page load) - the
    // browser already handles that correctly on its own, hash included.
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }

    if (location.hash) {
      const target = document.getElementById(location.hash.slice(1));
      if (target) target.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }

    if (navigationType === "POP") return;

    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, [location.pathname, location.hash, navigationType]);

  return null;
};

export default ScrollManager;
